/**
 * OverviewPage — the ERP-grade platform super-admin cockpit. Reads the two cross-lab aggregation endpoints
 * (GET /management/metrics/overview + /trends) and composes:
 *   • an ALERTS strip (only the alerts that fired — SLA breach / aged tickets / churn spike / onboarding
 *     stall / unread backlog), each deep-linking into the relevant section;
 *   • an expanded KPI grid (8 tiles) with period-over-period micro-trends (new labs, churn, …);
 *   • a CHART region (cumulative lab-growth line · subscription-status donut · tickets-by-category bar +
 *     ticket-status donut · onboarding funnel);
 *   • a period picker (30 j / 90 j / 12 mois) that drives the trends fetch + contextualises the deltas.
 *
 * Gating: the whole console is super-admin-only (the route rides <PlatformAdminGuard> + this screen renders
 * its own <ForbiddenState> defensively, keyed off `useIsPlatformAdmin()` — est_admin_plateforme ONLY, no
 * management.* capability fallback). Full 5-state DoD (Loading / Empty / Error / Offline / Forbidden) +
 * the cloud-only "not available on this install" short-circuit. Token-driven, AA, FR i18n.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  OfflineState,
} from "@/ui";
import { useT, useUiStore } from "@/app";
import { ALL_MANAGEMENT_CAPS } from "../caps";
import { isManagementAbsent } from "../api";
import { useOverviewMetrics, useTrendMetrics } from "../metrics.api";
import { useIsPlatformAdmin } from "../session";
import {
  deriveAlerts,
  delta,
  fmtCount,
  fmtPct,
  isEmptyDist,
  SUB_STATUT_COLOR,
  TICKET_STATUT_COLOR,
  toBarData,
  toDonutData,
} from "../metrics.lib";
import type { MetricsPeriod } from "../metrics.types";
import { PageHeader } from "../components/PageHeader";
import { Screen, TileGrid } from "../components/Screen";
import { MetricTile } from "../components/MetricTile";
import { AlertStrip } from "../components/AlertStrip";
import { MetricsPeriodSwitch } from "../components/MetricsPeriodSwitch";
import {
  CategoryBarChart,
  DistributionDonut,
  LabGrowthChart,
  OnboardingFunnel,
  type FunnelStage,
} from "../components/MetricsCharts";

/** A two-up responsive grid for the chart cards (collapses to one column on narrow / dense viewports). */
function ChartGrid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gap: "var(--nv-s-4)",
        gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
      }}
    >
      {children}
    </div>
  );
}

export function OverviewPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const online = useUiStore((s) => s.online);
  const isAdmin = useIsPlatformAdmin();
  const [period, setPeriod] = useState<MetricsPeriod>("30d");

  const enabled = isAdmin && online;
  const overview = useOverviewMetrics(enabled);
  const trends = useTrendMetrics(period, enabled);
  const m = overview.data;

  const refresh = () => void Promise.all([overview.refetch(), trends.refetch()]);

  const header = (
    <PageHeader
      title={t("mgmt.overview.title")}
      subtitle={t("mgmt.overview.subtitle")}
      actions={
        <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-3)", flexWrap: "wrap" }}>
          <MetricsPeriodSwitch value={period} onChange={setPeriod} />
          <Button variant="ghost" size="sm" onClick={refresh} disabled={overview.isFetching}>
            {t("mgmt.action.refresh")}
          </Button>
        </div>
      }
    />
  );

  // ── 5-state DoD (+ cloud-absent) ─────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <ForbiddenState
        requiredCapabilities={ALL_MANAGEMENT_CAPS}
        message="Cette console est réservée aux administrateurs de la plateforme Pharmakon."
      />
    );
  }

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={refresh} cached={!!m} />
      </Screen>
    );
  }

  if (overview.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState variant="table" rows={4} />
      </Screen>
    );
  }

  if (overview.isError) {
    // Cloud-only module on a local box → its routes 404/501. Surface the clean "not available here" notice.
    if (isManagementAbsent(overview.error)) {
      return (
        <Screen>
          {header}
          <Card title={t("mgmt.unavailable.title")}>
            <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>{t("mgmt.unavailable.desc")}</p>
          </Card>
        </Screen>
      );
    }
    return (
      <Screen>
        {header}
        <ErrorState message={overview.error.message} code={overview.error.code} onRetry={refresh} />
      </Screen>
    );
  }

  if (!m) {
    return (
      <Screen>
        {header}
        <EmptyState title={t("mgmt.overview.empty.title")} description={t("mgmt.overview.empty.desc")} />
      </Screen>
    );
  }

  // ── Derive the view models ────────────────────────────────────────────────────────
  const alerts = deriveAlerts(m);
  const labsDelta = delta(m.labs.newThisMonth, m.labs.newPrevMonth);
  const churnDelta = delta(m.subscriptions.churn30d, m.subscriptions.churnPrev30d);
  const ticketsDelta = delta(m.tickets.open, m.tickets.openPrevPeriod);

  // Labels are resolved against the always-registered overview namespace (so the donut/bar read correctly
  // even when the section message packs are not registered). A missing key falls back to the raw statut.
  const subDonut = toDonutData(m.subscriptions.statusDistribution, SUB_STATUT_COLOR, (k) =>
    t(`mgmt.overview.subStatut.${k}`),
  );
  const ticketStatusDonut = toDonutData(m.tickets.byStatut, TICKET_STATUT_COLOR, (k) =>
    t(`mgmt.overview.ticketStatut.${k}`),
  );
  const categoryBars = toBarData(m.tickets.byCategorie, (k) => t(`mgmt.overview.cat.${k}`));

  const funnelStages: FunnelStage[] = [
    { key: "provisioned", label: t("mgmt.overview.funnel.provisioned"), value: m.onboardingFunnel.provisioned },
    { key: "synced", label: t("mgmt.overview.funnel.synced"), value: m.onboardingFunnel.synced },
    { key: "activeSub", label: t("mgmt.overview.funnel.activeSub"), value: m.onboardingFunnel.activeSub },
    ...(m.onboardingFunnel.everLoggedIn != null
      ? [{ key: "everLoggedIn", label: t("mgmt.overview.funnel.everLoggedIn"), value: m.onboardingFunnel.everLoggedIn }]
      : []),
  ];

  const periodLabel = t(`mgmt.overview.period.${period}`);

  return (
    <Screen>
      {header}

      <AlertStrip alerts={alerts} />

      <TileGrid min={220}>
        <MetricTile
          label={t("mgmt.overview.kpi.labs")}
          value={fmtCount(m.labs.total)}
          hint={t("mgmt.overview.kpi.labs.hint")}
          tone="accent"
          icon="⬡"
          delta={labsDelta}
          goodWhen="up"
          deltaContext={t("mgmt.overview.delta.vsPrevMonth")}
        />
        <MetricTile
          label={t("mgmt.overview.kpi.subscriptions")}
          value={fmtCount(m.subscriptions.active)}
          hint={t("mgmt.overview.kpi.subscriptions.hint")}
          tone="success"
          icon="◆"
        />
        <MetricTile
          label={t("mgmt.overview.kpi.churn")}
          value={fmtCount(m.subscriptions.churn30d)}
          hint={t("mgmt.overview.kpi.churn.hint")}
          tone={m.subscriptions.churn30d > 0 ? "warning" : "neutral"}
          icon="↓"
          delta={churnDelta}
          goodWhen="down"
          deltaContext={t("mgmt.overview.delta.vsPrev30d")}
        />
        <MetricTile
          label={t("mgmt.overview.kpi.tickets")}
          value={fmtCount(m.tickets.open)}
          hint={t("mgmt.overview.kpi.tickets.hint")}
          tone="warning"
          icon="✉"
          delta={ticketsDelta}
          goodWhen="down"
          deltaContext={t("mgmt.overview.delta.vsPrev30d")}
        />
        <MetricTile
          label={t("mgmt.overview.kpi.sla")}
          value={m.tickets.slaPct == null ? "—" : fmtPct(m.tickets.slaPct)}
          hint={t("mgmt.overview.kpi.sla.hint", { h: m.tickets.slaTargetHours })}
          tone={
            m.tickets.slaPct == null
              ? "neutral"
              : m.tickets.slaPct < 75
                ? "danger"
                : m.tickets.slaPct < 90
                  ? "warning"
                  : "success"
          }
          icon="◷"
        />
        <MetricTile
          label={t("mgmt.overview.kpi.aged")}
          value={fmtCount(m.tickets.agedOpen)}
          hint={t("mgmt.overview.kpi.aged.hint", { d: m.tickets.agedDays })}
          tone={m.tickets.agedOpen > 0 ? "warning" : "neutral"}
          icon="⏳"
        />
        <MetricTile
          label={t("mgmt.overview.kpi.suspended")}
          value={fmtCount(m.labs.suspended)}
          hint={t("mgmt.overview.kpi.suspended.hint")}
          tone={m.labs.suspended > 0 ? "warning" : "neutral"}
          icon="⊘"
        />
        <MetricTile
          label={t("mgmt.overview.kpi.unread")}
          value={fmtCount(m.tickets.unread)}
          hint={t("mgmt.overview.kpi.unread.hint")}
          tone="neutral"
          icon="●"
        />
      </TileGrid>

      <ChartGrid>
        <Card title={t("mgmt.overview.chart.labGrowth.title")} actions={<Subtitle text={periodLabel} />}>
          <LabGrowthChart series={trends.data?.series.cumulativeLabs ?? []} bucket={trends.data?.bucket ?? "day"} />
        </Card>

        <Card title={t("mgmt.overview.chart.subscriptions.title")}>
          {isEmptyDist(m.subscriptions.statusDistribution) ? (
            <EmptyState title={t("mgmt.overview.chart.empty.title")} description={t("mgmt.overview.chart.empty.desc")} />
          ) : (
            <DistributionDonut data={subDonut} ariaLabel={t("mgmt.overview.chart.subscriptions.aria")} />
          )}
        </Card>

        <Card title={t("mgmt.overview.chart.byCategory.title")}>
          <CategoryBarChart data={categoryBars} ariaLabel={t("mgmt.overview.chart.byCategory.aria")} />
        </Card>

        <Card title={t("mgmt.overview.chart.ticketStatus.title")}>
          {isEmptyDist(m.tickets.byStatut) ? (
            <EmptyState title={t("mgmt.overview.chart.empty.title")} description={t("mgmt.overview.chart.empty.desc")} />
          ) : (
            <DistributionDonut data={ticketStatusDonut} ariaLabel={t("mgmt.overview.chart.ticketStatus.aria")} />
          )}
        </Card>

        <Card title={t("mgmt.overview.chart.funnel.title")}>
          <OnboardingFunnel stages={funnelStages} />
        </Card>

        <Card title={t("mgmt.overview.quick.title")}>
          <div style={{ display: "flex", gap: "var(--nv-s-2)", flexWrap: "wrap" }}>
            <Button variant="secondary" onClick={() => navigate("/management/laboratoires")}>
              {t("mgmt.overview.quick.onboarding")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/management/types-parametres")}>
              {t("mgmt.overview.quick.paramtypes")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/management/abonnements")}>
              {t("mgmt.overview.quick.subscriptions")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/management/support")}>
              {t("mgmt.overview.quick.support")}
            </Button>
          </div>
        </Card>
      </ChartGrid>

      <p style={{ margin: 0, fontSize: "var(--nv-fs-caption)", color: "var(--nv-steel)" }}>
        {t("mgmt.overview.limits.note")}
      </p>
    </Screen>
  );
}

/** A small slate caption used as a Card action (the period context on the growth chart). */
function Subtitle({ text }: { text: string }) {
  return (
    <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-steel)" }}>{text}</span>
  );
}
